import { useCallback, useState } from 'react';
import { useSWRConfig } from 'swr';
import { APPROVAL_V2_URL } from '@/constant/approval';
import { postDataFetcher } from '@/utils/apis';

export type UseWithdrawApprovalOptions = {
  onSuccess?: (response: Record<string, unknown>) => void;
  onError?: (error: unknown) => void;
};

/**
 * 품의 회수 mutation.
 * - POST /api/v2/approval/{documentId}/withdraw
 * - 회수 성공 시 문서는 반려(R)로 종결된다. 다시 올리려면 신규 작성으로 재상신.
 * - 권한: 기안자 본인만. 본인이 아니면 서버가 403 → 호출부에서 에러 토스트.
 * - 응답 코드:
 *   200 회수 성공
 *   403 기안자가 아님
 *   404 문서를 찾을 수 없음
 *   422 회수 불가 상태(이미 누군가 결재 / 진행중·보류 외 상태)
 */
const useWithdrawApproval = (options?: UseWithdrawApprovalOptions) => {
  const { mutate } = useSWRConfig();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const withdraw = useCallback(
    async (
      documentId: string,
      reason: string,
    ): Promise<Record<string, unknown>> => {
      setIsSubmitting(true);
      const baseKey = `${APPROVAL_V2_URL}/${documentId}`;
      try {
        const response = await postDataFetcher<
          Record<string, unknown>,
          Record<string, unknown>
        >(`${baseKey}/withdraw`, { reason });
        // 단건 캐시 무효화 + 재검증 — 문자열 key(useApproval) + 튜플 key(useApprovalView) 동시 매칭.
        await mutate(
          (cacheKey) =>
            cacheKey === baseKey ||
            (Array.isArray(cacheKey) && cacheKey[0] === baseKey),
        );
        options?.onSuccess?.(response);
        return response;
      } catch (error) {
        options?.onError?.(error);
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    [mutate, options],
  );

  return { withdraw, isSubmitting };
};

export default useWithdrawApproval;
